import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { ChevronLeft, ChevronRight, PackageSearch, Tag } from 'lucide-react'

import api from '../../../const/axios'
import { loadingOff, loadingOn, setPackages } from '../../../redux/actions'
import { withRouter } from '../../../utils/withRouter'
import { connect } from 'react-redux'
import Package from '../../Elements/Package/Package'
import { cn } from '@/lib/utils'
import { usePageParam } from '@/hooks/usePageParam'

const PAGE_SIZE = 15

interface TagPackagesProps {
  params: { id: string }
  loadingOn: () => void
  loadingOff: () => void
  setPackages: (p: any[]) => void
}

const TagPackages: React.FC<TagPackagesProps> = ({
  params,
  loadingOn,
  loadingOff,
  setPackages,
}) => {
  const [packages, setLocal] = useState<any[]>([])
  const [total, setTotal]     = useState(0)
  const [tagName, setTagName] = useState('')
  const [loading, setLoading] = useState(false)

  // Page lives in the URL (?page=N) so it survives back/forward navigation
  const { page, goToPage } = usePageParam()

  const fetchPage = useCallback(async (pg: number) => {
    setLoading(true)
    loadingOn()
    try {
      const res = await api.get(`/packages/tag?id=${params.id}&page=${pg}&perPage=${PAGE_SIZE}`)
      setLocal(res.data.packages ?? [])
      setPackages(res.data.packages ?? [])
      setTotal(res.data.count ?? 0)
      setTagName(res.data.name ?? '')
    } finally {
      setLoading(false)
      loadingOff()
    }
  }, [params.id, loadingOn, loadingOff, setPackages])

  useEffect(() => { fetchPage(page) }, [fetchPage, page])

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>{`${tagName} | Paketlər | 3sual.az`}</title>
        <meta name="robots" content="index" />
      </Helmet>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4'>
        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-xl font-black text-gray-900 dark:text-gray-50 flex items-center gap-2">
            <Tag className="w-5 h-5 text-blue-600 dark:text-indigo-300" />
            {tagName || '…'}{" "}
            {total > 0 && (
              <span className="text-base font-semibold text-gray-400 dark:text-gray-500">({total})</span>
            )}
          </h1>
        </div>

        {/* ── Grid ─────────────────────────────────────────────────────── */}
        {packages.length > 0 ? (
          <>
            <div className={cn(
              'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4',
              loading && 'opacity-60 pointer-events-none transition-opacity'
            )}>
              {packages.map((item: any) => (
                <Link key={item.id} to={`/packages/${item.id}`} className="block">
                  <Package pack={item} free={true} />
                </Link>
              ))}
            </div>

            {/* ── Pagination ───────────────────────────────────────── */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1}
                  className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:border-slate-400 dark:hover:border-slate-600 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                  .reduce<(number | 'e')[]>((acc, p, i, arr) => {
                    if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('e')
                    acc.push(p)
                    return acc
                  }, [])
                  .map((p, i) =>
                    p === 'e' ? (
                      <span key={`e${i}`} className="w-9 text-center text-gray-400 dark:text-gray-500 text-sm">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => goToPage(p as number)}
                        className={cn(
                          'w-9 h-9 rounded-lg text-sm font-medium border transition-colors',
                          page === p
                            ? 'bg-slate-700 text-white border-slate-700 shadow-sm'
                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-slate-400 dark:hover:border-slate-600 hover:text-slate-700'
                        )}
                      >
                        {p}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => goToPage(page + 1)}
                    disabled={page === totalPages}
                    className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:border-slate-400 dark:hover:border-slate-600 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          ) : !loading ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <PackageSearch className="w-14 h-14 text-gray-200 mb-4" />
              <p className="text-gray-400 dark:text-gray-500 font-medium text-lg">Heç bir paket tapılmadı</p>
              <p className="text-gray-300 text-sm mt-1">Bu teqlə işarələnmiş paket yoxdur</p>
            </div>
          ) : null}
      </div>
    </div>
  )
}

export default withRouter(connect(null, { loadingOn, loadingOff, setPackages })(TagPackages))
